import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ValidationError } from '@ngx-pwa/local-storage';
import {
  AddressAttributes,
  AddressKindEnum,
  ShoppingCart,
  UpdateShippingInformationAttributes
} from '@queries/generated';
import { ShoppingCartService } from '@services/shopping-cart.service';
import { Fade } from '@util/animations';
import { pluck } from '@util/pluck';
import equal from 'fast-deep-equal';
import { combineLatest, noop } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'vnm-checkout-order',
  templateUrl: './checkout-order.component.html',
  styleUrls: ['./checkout-order.component.scss'],
  animations: [Fade]
})
export class CheckoutOrderComponent implements OnInit {
  @Input() shoppingCart!: ShoppingCart;
  @Output() canDeactivate = new EventEmitter<boolean>();

  orderForm = this.fb.group({
    billingAddress: [null, Validators.required],
    shippingAddress: [],
    shippingInformation: []
  });
  errors: ValidationError[];

  sameAddress = false;

  constructor(private fb: UntypedFormBuilder, private shoppingCartService: ShoppingCartService) {}

  ngOnInit() {
    /// This is going to define validation for the attributes of the form, if required.
    this.setFormValidators();

    /// This is going to listen to the changes in the form and update the address accordingly.
    this.listenToFormChanges();

    this.canDeactivate.emit(this.orderForm.valid);
  }

  onToggleShipping({ checked: sameAsBilling }) {
    const { billingAddress, shippingAddress } = this.orderForm.controls;

    if (sameAsBilling) {
      shippingAddress.patchValue(billingAddress.value);
      shippingAddress.disable();
      this.updateAddress(shippingAddress.value, AddressKindEnum.Shipping);
    } else {
      shippingAddress.reset({});
      shippingAddress.enable();
    }

    this.orderForm.updateValueAndValidity();
  }

  /**
   * Based on the shopping cart, set the form validators for the shipping data.
   */
  private setFormValidators() {
    const { billingAddress, shippingAddress, requiresShipping, shippingInformation } = this.shoppingCart;

    this.sameAddress = billingAddress && equal(billingAddress, shippingAddress);

    if (this.sameAddress) {
      this.orderForm.get('shippingAddress').disable();
    } else {
      this.orderForm.get('shippingAddress').enable();
    }
    if (requiresShipping) {
      this.orderForm.get('shippingInformation').setValidators(Validators.required);
      this.orderForm.get('shippingAddress').setValidators(Validators.required);
      this.orderForm.get('shippingInformation').updateValueAndValidity();
      this.orderForm.get('shippingAddress').updateValueAndValidity();
      this.orderForm.updateValueAndValidity();
    }
    this.orderForm.patchValue({ billingAddress, shippingAddress, shippingInformation });

    this.canDeactivate.emit(this.orderForm.valid);
  }

  /**
   * Subscribe to all the subforms and listen to changes, updating async things on the shopping cart.
   */
  private listenToFormChanges() {
    const { billingAddress, shippingAddress, shippingInformation } = this.orderForm.controls;

    this.orderForm.statusChanges
      .pipe(untilDestroyed(this), debounceTime(500))
      .subscribe((status) => this.canDeactivate.emit(this.orderForm.valid));

    combineLatest([billingAddress.statusChanges, billingAddress.valueChanges])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        debounceTime(500),
        filter(([status, _data]) => status === 'VALID')
      )
      .subscribe(([_status, data]) => this.updateAddress(data, AddressKindEnum.Billing, this.sameAddress));

    combineLatest([shippingAddress.statusChanges, shippingAddress.valueChanges])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        debounceTime(500),
        filter(([status, _data]) => status === 'VALID' && !this.sameAddress)
      )
      .subscribe(([_status, value]) => this.updateAddress(value, AddressKindEnum.Shipping));

    combineLatest([shippingInformation.statusChanges, shippingInformation.valueChanges])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        debounceTime(500),
        filter(([status, _data]) => status === 'VALID')
      )
      .subscribe(([status, data]) => this.updateShippingInformation(data));
  }

  private updateAddress(address: AddressAttributes, kind: AddressKindEnum, sameAddress = false) {
    this.shoppingCartService
      .updateAddress({ patch: { address: this.sanitizeValue(address), kind, sameAddress } })
      .subscribe(
        () => noop,
        (errors) => (this.errors = errors)
      );
  }

  private updateShippingInformation(attributes: UpdateShippingInformationAttributes) {
    const patch = pluck(attributes, 'companyName', 'email', 'name', 'phoneNumber');

    this.shoppingCartService.updateShippingInformation({ patch }).subscribe(
      () => noop,
      (errors) => {
        this.errors = errors;
      }
    );
  }

  private sanitizeValue(value: AddressAttributes): AddressAttributes {
    return {
      ...pluck(value, 'address1', 'address2', 'city', 'postalCode'),
      country: { code: value?.country?.code },
      province: { code: value?.province?.code }
    };
  }
}
